import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Empty, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlobalContext } from '../../App';
import { useCC } from '../../contexts/CCContext';
import CCPublicationControls from './CCPublicationControls';
import CCAdminSelector from './CCAdminSelector';
import { CCCoverage, CCResourceState } from './CCFeedback';
import { cropLabel } from './publicationContract';
import CCComparison from './CCComparison';
import CCTerritories from './CCTerritories';

export default function CCDashboard() {
  const cc = useCC(); const { t, i18n } = useTranslation('translation'); const navigate = useNavigate();
  const { setInvestigatePolygon, setGlobalCCViewEnabled } = useContext(GlobalContext);
  const [basis, setBasis] = useState('classified');
  const featureCard = useRef(null);
  const resultsPanel = useRef(null);
  const sidebarScope = cc.sidebarScope || cc.scope;
  useEffect(() => {
    if (resultsPanel.current) resultsPanel.current.scrollTop = 0;
  }, [sidebarScope?.kind, sidebarScope?.subject_id, cc.accessKey]);
  useEffect(() => {
    if (cc.selectedPolygon) featureCard.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [cc.selectedPolygon]);
  const format = (value) => value == null ? '—' : new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(value);
  const modes = [['explore', 'explore'], ['me', 'view_my_territory'], ['commercial', 'view_commercial_regions'], ['manage', 'manage_regions']]
    .filter(([, capability]) => cc.can(capability)).map(([value]) => ({ value, label: t(`ci.mode${value}`) }));
  const data = cc.sidebar ? cc.sidebar.data : cc.stats.data;
  const resultSnapshot = cc.sidebar?.snapshot || cc.snapshot;
  const controlSnapshot = cc.controlSnapshot || cc.snapshot || resultSnapshot;
  const classes = (controlSnapshot?.classes || []).filter((entry) => controlSnapshot.present_class_codes.includes(entry.code))
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  return <div className="ci-dashboard">
    <div className="ci-panel-controls">
      <details className="ci-filter-details" open><summary>{t('ci.selectionControls')}</summary>
        <CCPublicationControls />
        {modes.length > 1 && <label><span className="ci-label">{t('ci.mode')}</span><Select className="ci-wide" aria-label={t('ci.mode')} value={cc.mode} options={modes} onChange={cc.selectMode} /></label>}
        {controlSnapshot && cc.mode !== 'manage' && <CCAdminSelector />}
        {controlSnapshot && cc.mode !== 'manage' && <label><span className="ci-label">{t('ci.cropFilter')}</span>
          <Select aria-label={t('ci.cropFilter')} className="ci-wide" allowClear placeholder={t('ci.allClasses')}
            disabled={!cc.snapshot}
            value={cc.classCode ?? undefined} onChange={(value) => cc.setClassCode(value ?? null)}
            options={classes.map((entry) => ({ value: entry.code, label: cropLabel(entry, t, i18n.language), disabled: cc.excludedClassKeys?.includes(entry.semantic_key) }))} />
        </label>}
      </details>
    </div>
    <div ref={resultsPanel} className="ci-panel-results">
      {['me', 'commercial', 'manage'].includes(cc.mode) && cc.permission && <CCTerritories />}
      {controlSnapshot && cc.mode !== 'manage' && <>
        <div className="ci-area-heading"><span className="ci-label">{t('ci.selectedArea')}</span><h3>{sidebarScope?.name || t(`ci.mode${cc.mode}`)}</h3></div>
        {!!cc.excludedClassKeys?.length && <div className="ci-exclusion-summary" role="status">
          {t('ci.filteredAnalytics', { count: cc.excludedClassKeys.length })}
          <Button type="link" size="small" onClick={cc.clearCropExclusions}>{t('ci.showAllCrops')}</Button>
        </div>}
        {cc.sidebarPrevious && <p className="ci-muted" role="status">{t('ci.previousPublication', { year: resultSnapshot.year, title: resultSnapshot.title })}</p>}
        <CCCoverage value={data?.coverage || resultSnapshot.coverage} snapshot={resultSnapshot} />
        {!sidebarScope && !cc.navigation?.loading && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('general.region_not_selected')} />}
        {(cc.stats.status !== 'loading' || !cc.loading) && <CCResourceState result={cc.stats} retry={cc.retryStats || cc.retry} />}
        {data && data.coverage.status !== 'UNAVAILABLE' && <>
          <div className="ci-totals"><div><span>{t('ci.classified')}</span><strong>{format(data.classified_area_ha)} <small>ha</small></strong></div>
            <div><span>{t('cc.agriculturalArea')}</span><strong>{format(data.agricultural_area_ha)} <small>ha</small></strong></div></div>
          <section className="ci-distribution">
            <div className="ci-section-heading"><h3>{t('ci.cropDistribution')}</h3>
              {cc.classCode != null && <Button size="small" type="link" onClick={() => cc.setClassCode(null)}>{t('ci.clearFilter')}</Button>}
            </div>
            <label className="ci-share-control"><span className="ci-label">{t('ci.shareBasis')}</span>
              <Select aria-label={t('ci.shareBasis')} value={basis} onChange={setBasis} options={[
                { value: 'classified', label: t('ci.classifiedShare') }, { value: 'agricultural', label: t('ci.agriculturalShare') },
              ]} />
            </label>
            <table className="ci-distribution-table"><thead><tr><th>{t('cc.class')}</th><th>{t('ci.areaHa')}</th><th>%</th></tr></thead>
              <tbody>{[...data.classes].sort((a, b) => b.area_ha - a.area_ha).map((row) => {
                const entry = resultSnapshot.classes.find((c) => c.code === row.code);
                const share = row[`share_of_${basis}_area_pct`];
                return <tr key={row.code} className={!cc.sidebarPrevious && row.code === cc.classCode ? 'ci-selected-row' : undefined}>
                  <th scope="row"><button type="button" disabled={cc.sidebarPrevious} aria-pressed={!cc.sidebarPrevious && row.code === cc.classCode} onClick={() => cc.setClassCode(row.code === cc.classCode ? null : row.code)}>
                    <i className="cc-swatch" style={{ backgroundColor: entry?.fill, borderColor: entry?.stroke }} />
                    <span>{cropLabel(entry, t, i18n.language)}{!entry?.is_crop && <small className="ci-muted"> · {t('ci.background')}</small>}</span>
                  </button></th><td>{format(row.area_ha)}</td><td>{format(share)}</td>
                </tr>;
              })}</tbody>
            </table>
            <p className="ci-muted">{t('ci.filterHelp')}</p>
          </section>
        </>}
        {cc.selectedPolygon && <section ref={featureCard} className="ci-feature-card" aria-label={t('ci.selectedFeature')}>
          <div className="ci-section-heading"><h3>{t('ci.selectedFeature')}</h3><Button type="text" size="small" onClick={() => cc.setSelectedPolygon(null)}>{t('ci.close')}</Button></div>
          <strong>{cropLabel(cc.snapshot.classes.find((c) => c.code === cc.selectedPolygon.properties.crop_code), t, i18n.language)}</strong>
          <p>{t('cc.polygonArea')}: {format(cc.selectedPolygon.properties.area_ha)} ha · {cc.snapshot.year}</p>
          <p className="ci-muted">{t('ci.featureAreaHelp')}</p>
          {cc.selectedPolygon.geometry_clipped_to_bbox !== false && <Alert type="info" message={t('ci.investigationUnavailable')} />}
          <Button disabled={cc.selectedPolygon.geometry_clipped_to_bbox !== false} onClick={() => {
            setInvestigatePolygon({ ...cc.selectedPolygon, properties: { ...cc.selectedPolygon.properties, asset_type: 'cc_polygon', dataset_id: cc.snapshot.snapshot_id } });
            setGlobalCCViewEnabled(false); navigate('/investigate');
          }}>{t('cc.investigate')}</Button>
        </section>}
        <CCComparison />
      </>}
    </div>
  </div>;
}
